import React, {useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {Editor} from '@tiptap/react';
import ColorSelectCompact from '../../../../incase-domain/components/select/compact/color/colorSelectCompact/ColorSelectCompact';
import FormattingToolbar from './components/FormattingToolbar';
import FontToolbar from './components/FontToolbar';
import TextFormattingToolbar from './components/TextFormattingToolbar';
import AdditionalFormatingToolbar from './components/AdditionalFormatingToolbar';
import {editorModeSelector} from '../../../../store/selectors/tiptapEditor.selectors';
import {EditorModeEnum} from '../../../../store/actions/types/tiptapEditor.types';

interface ITipTapProps {
    editor: Editor;
}

function TipTapToolbar({editor}: ITipTapProps): JSX.Element | null {
    const {t} = useTranslation();
    const editorMode = useSelector(editorModeSelector);

    const isFormControl = useCallback((element: HTMLElement | null) => {
        const formTags = ['INPUT', 'SELECT', 'TEXTAREA'];
        let target = element;

        while (target && target !== document.body) {
            if (
                formTags.includes(target.tagName) ||
                target.getAttribute('role') === 'listbox' ||
                target.getAttribute('role') === 'combobox'
            ) {
                return true;
            }
            target = target.parentElement;
        }
        return false;
    }, []);

    const handleMouseDown = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            if (!isFormControl(e.target as HTMLElement)) {
                e.preventDefault();
            }
        },
        [isFormControl]
    );

    const handleColorChange = (color: string | null | undefined): void => {
        if (!editor) return;

        if (editorMode === EditorModeEnum.SELECT) {
            const chain = editor.chain().selectAll();
            if (color) {
                chain.setColor(color).updateAttributes('listItem', {color}).run();
            } else {
                chain.unsetColor().updateAttributes('listItem', {color: null}).run();
            }
            return;
        }

        const {from, to, empty} = editor.state.selection;
        const hasSelection = !empty && from !== to;

        let chain = editor.chain().focus();

        if (!hasSelection) {
            chain = chain.selectAll();
        }

        if (color) {
            chain = chain.setColor(color).updateAttributes('listItem', {color});
        } else {
            chain = chain.unsetColor().updateAttributes('listItem', {color: null});
        }

        chain.run();
    };

    const getSelectedTextColor = (): string | undefined => {
        if (!editor) return undefined;

        const {from, to, empty} = editor.state.selection;
        if (empty) return editor.getAttributes('textStyle').color;

        const colors = new Set<string>();
        editor.state.doc.nodesBetween(from, to, (node) => {
            const color = node.attrs.textStyle?.color || node.marks?.find((mark) => mark.type.name === 'textStyle')?.attrs.color;
            if (color) colors.add(color);
            return true;
        });

        if (colors.size > 1) return undefined;
        return colors.size === 1 ? Array.from(colors)[0] : editor.getAttributes('textStyle').color;
    };

    return (
        <div onMouseDown={handleMouseDown}>
            <div className="u-flex u-flex-col u-gap-2">
                <FontToolbar editor={editor} />
                <FormattingToolbar editor={editor} />
                <TextFormattingToolbar editor={editor} />
                <AdditionalFormatingToolbar editor={editor} />
                <ColorSelectCompact label={t(`designSidepanel.fontColor`)} color={getSelectedTextColor()} onChange={handleColorChange} />
            </div>
        </div>
    );
}

export default TipTapToolbar;
